// Hunger, saturation & natural regen (PLR-4). PURE (ENG-1).
//
// Exhaustion accrues from activity (the host adds it: sprint/jump/attack/damage);
// each EXHAUSTION_PER_DRAIN spent drops one saturation, or one hunger when
// saturation is empty. At hunger ≥ 18 the player slowly regenerates (costing
// exhaustion); at hunger 0 they starve toward the difficulty floor. Eating (food
// restore) lands with farming in M11 — until then hunger only drains.
import { SURVIVAL } from '../config';
import type { Player } from './player';

/** Difficulty-dependent starvation floor (health it will not drop below). */
const STARVE_FLOOR: Record<string, number> = { peaceful: 20, easy: 10, normal: 1, hard: 0 };

/** Advance hunger/saturation/regen/starvation one tick. `difficulty` gates the
 *  starvation floor (default normal). No-op outside survival/adventure. */
export function tickHunger(player: Player, difficulty = 'normal'): void {
  if (!player.caps.survivalHunger) return;

  // Spend accrued exhaustion: saturation first, then hunger.
  while (player.exhaustion >= SURVIVAL.EXHAUSTION_PER_DRAIN) {
    player.exhaustion -= SURVIVAL.EXHAUSTION_PER_DRAIN;
    if (player.saturation > 0) player.saturation = Math.max(0, player.saturation - 1);
    else player.hunger = Math.max(0, player.hunger - 1);
  }

  // Natural regen: +1 health / REGEN_TICKS while well-fed, costing exhaustion.
  if (player.hunger >= SURVIVAL.REGEN_HUNGER && player.health < player.maxHealth) {
    if (++player.regenTimer >= SURVIVAL.REGEN_TICKS) {
      player.regenTimer = 0;
      player.heal(1);
      player.addExhaustion(6);
    }
  } else {
    player.regenTimer = 0;
  }

  // Starvation: −1 / STARVE_TICKS at hunger 0, down to the difficulty floor.
  if (player.hunger <= 0) {
    const floor = STARVE_FLOOR[difficulty] ?? 1;
    if (++player.starveTimer >= SURVIVAL.STARVE_TICKS) {
      player.starveTimer = 0;
      if (player.health > floor) player.applyDamage(1);
    }
  } else {
    player.starveTimer = 0;
  }
}
